import React, {
	useState, useEffect, useContext, useRef
} from 'react';
import classNames from 'classnames';

import Link from '../Link';

import { AuthContext } from '../../context/auth-context';
import { PRACTITIONER_STATUS } from '../../data/constants';
import { getMessagingForProtectedContent } from '../../utils/messaging';

import renderIcon from '../../utils/render-icon';
import QuickSearch from '../QuickSearch';

const ns = `nav-mobile-menu`;
const submenuControlIds = {
	Learn: 'mobile-navigation-submenu-learn',
	Shop: 'mobile-navigation-submenu-shop',
	'Why Nordic': 'mobile-navigation-submenu-why-nordic',
};

const compactFocusableElements = elements => {
	return elements.filter(element => {
		return element && typeof element.focus === 'function';
	});
};

const NavMobileMenu = ({id, onCloseCallback, open, menuItems, location}) => {
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [mobileNavOpened, setMobileNavOpened] = useState(false);
	const [subNavOpened, setSubNavOpened] = useState(false);
	const [currentSubNavOpened, setCurrentSubNavOpened] = useState('');
	const mobileSearchButtonRef = useRef(null);
	const previouslyFocusedElementRef = useRef(null);
	const parentMenuButtonRefs = useRef({});
	const accountLinkRef = useRef(null);
	const createAccountLinkRef = useRef(null);
	const submenuBackButtonRefs = useRef({});
	const submenuLinkRefs = useRef({
		Learn: [],
		Shop: [],
		'Why Nordic': [],
	});
	const activeSubmenuTriggerRef = useRef(null);

	const { state: authState } = useContext(AuthContext);
	const { auth, practitioner } = authState;
	const practitionerStatus = practitioner.status || '';

	const isUserStatusActive = practitionerStatus.toUpperCase() === PRACTITIONER_STATUS.ACTIVE;

	useEffect(() => {
		setMobileNavOpened(open);
	}, [open]);

	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`mobilenav-opened`]: mobileNavOpened,
		[`mobilenav-closed`]: !mobileNavOpened,
	});

	const isParentMenuInteractive = mobileNavOpened && !subNavOpened;
	const isShopSubmenuInteractive = mobileNavOpened && currentSubNavOpened === 'Shop';
	const isWhyNordicSubmenuInteractive = mobileNavOpened && currentSubNavOpened === 'Why Nordic';
	const isLearnSubmenuInteractive = mobileNavOpened && currentSubNavOpened === 'Learn';

	const openSubNav = (e, subNav) => {
		activeSubmenuTriggerRef.current = e.currentTarget;
		setSubNavOpened(true);
		setCurrentSubNavOpened(subNav);
	};

	const closeSubNav = () => {
		setSubNavOpened(false);
		setCurrentSubNavOpened('');

		window.requestAnimationFrame(() => {
			activeSubmenuTriggerRef.current?.focus();
		});
	};

	const closeMobileMenu = e => {
		if (e.target.classList[0] === ns) {
			setMobileNavOpened(false);
			onCloseCallback();
		}
	};

	const closeAllNavs = () => {
		setMobileNavOpened(false);
		setSubNavOpened(false);
		setCurrentSubNavOpened('');
		setIsSearchOpen(false);
		onCloseCallback();
	};

	const toggleMobileSearch = () => {
		setIsSearchOpen(!isSearchOpen);
	};

	const handleSubmitCloses = () => {
		setMobileNavOpened(false);
		setSubNavOpened(false);
		setCurrentSubNavOpened('');
		setIsSearchOpen(false);
		onCloseCallback();
	};

	const setSubmenuLinkRef = (submenu, index, node) => {
		submenuLinkRefs.current[submenu][index] = node;
	};

	const getShopSectionLinkRefIndex = (sectionIndex, linkIndex) => {
		const primaryLinkCount = menuItems.shop?.[0]?.links?.length ?? 0;
		const previousSectionLinkCount = (menuItems.shop ?? [])
			.slice(1, sectionIndex)
			.reduce((count, section) => {
				return count + (section.links?.length ?? 0);
			}, 0);

		return primaryLinkCount + previousSectionLinkCount + linkIndex;
	};

	const getActiveSubmenuLinks = () => {
		return submenuLinkRefs.current[currentSubNavOpened] ?? [];
	};

	const getActiveFocusableElements = () => {
		if (subNavOpened && currentSubNavOpened) {
			return compactFocusableElements([
				submenuBackButtonRefs.current[currentSubNavOpened],
				...getActiveSubmenuLinks(),
			]);
		}

		return compactFocusableElements([
			mobileSearchButtonRef.current,
			parentMenuButtonRefs.current.Shop,
			parentMenuButtonRefs.current.Learn,
			parentMenuButtonRefs.current['Why Nordic'],
			accountLinkRef.current,
			createAccountLinkRef.current,
		]);
	};

	useEffect(() => {
		if (!mobileNavOpened) {
			return undefined;
		}

		previouslyFocusedElementRef.current = document.activeElement;

		const animationFrame = window.requestAnimationFrame(() => {
			const [firstFocusableElement] = getActiveFocusableElements();
			firstFocusableElement?.focus();
		});

		return () => {
			window.cancelAnimationFrame(animationFrame);
		};
	}, [mobileNavOpened]);

	useEffect(() => {
		if (mobileNavOpened) {
			return;
		}

		setSubNavOpened(false);
		setCurrentSubNavOpened('');
		setIsSearchOpen(false);

		if (
			previouslyFocusedElementRef.current
			&& typeof previouslyFocusedElementRef.current.focus === 'function'
		) {
			previouslyFocusedElementRef.current.focus();
		}
	}, [mobileNavOpened]);

	const handleDialogKeyDown = event => {
		if (event.key === 'Escape') {
			event.preventDefault();

			if (subNavOpened) {
				closeSubNav();
				return;
			}

			closeAllNavs();
			return;
		}

		if (event.key !== 'Tab') {
			return;
		}

		const focusableElements = getActiveFocusableElements();

		if (!focusableElements.length) {
			event.preventDefault();
			return;
		}

		const firstElement = focusableElements[0];
		const lastElement = focusableElements[focusableElements.length - 1];

		if (event.shiftKey && document.activeElement === firstElement) {
			event.preventDefault();
			lastElement.focus();
			return;
		}

		if (!event.shiftKey && document.activeElement === lastElement) {
			event.preventDefault();
			firstElement.focus();
		}
	};

	useEffect(() => {
		if (!subNavOpened || !currentSubNavOpened) {
			return undefined;
		}

		const animationFrame = window.requestAnimationFrame(() => {
			submenuBackButtonRefs.current[currentSubNavOpened]?.focus({
				preventScroll: true,
			});
		});

		return () => {
			window.cancelAnimationFrame(animationFrame);
		};
	}, [currentSubNavOpened, subNavOpened]);

	return (
		// eslint-disable-next-line
		<div
			aria-hidden={!mobileNavOpened}
			aria-modal={mobileNavOpened ? 'true' : undefined}
			className={rootClassnames}
			id={id}
			role={mobileNavOpened ? 'dialog' : undefined}
			aria-label="Mobile navigation menu"
			onClick={(e) => { return closeMobileMenu(e); }}
			onKeyDown={handleDialogKeyDown}
			tabIndex={-1}
		>
			<div className={`${ ns }__container `} tabIndex={-1}>
				<div
					aria-hidden={!isParentMenuInteractive}
					className={`${ ns }__parent-menu ${ subNavOpened ? 'sub-nav-opened' : '' }`}
					tabIndex={-1}
				>
					<div className={`${ ns }__menu-search`}>
						<div className={`${ ns }__quicksearch ${ isSearchOpen ? 'search-open' : '' }`}>
							<QuickSearch
								handleSearchOpen={toggleMobileSearch}
								handleMobileNavClose={handleSubmitCloses}
								location={location}
								searchButtonRef={mobileSearchButtonRef}
								searchControlsEnabled={isParentMenuInteractive}
							/>
						</div>
					</div>
					<button
						aria-controls={submenuControlIds.Shop}
						aria-expanded={currentSubNavOpened === 'Shop'}
						aria-haspopup="menu"
						className={`${ ns }__parent-menu-item`}
						ref={node => {
							parentMenuButtonRefs.current.Shop = node;
						}}
						onClick={e => { return openSubNav(e, 'Shop'); }}
						tabIndex={isParentMenuInteractive ? 0 : -1}
						type="button"
					>
						<p>Shop</p>
						{renderIcon('carrot-right')}
					</button>
					<button
						aria-controls={submenuControlIds.Learn}
						aria-expanded={currentSubNavOpened === 'Learn'}
						aria-haspopup="menu"
						className={`${ ns }__parent-menu-item`}
						ref={node => {
							parentMenuButtonRefs.current.Learn = node;
						}}
						onClick={e => { return openSubNav(e, 'Learn'); }}
						tabIndex={isParentMenuInteractive ? 0 : -1}
						type="button"
					>
						<p>Learn</p>
						{renderIcon('carrot-right')}
					</button>
					<button
						aria-controls={submenuControlIds['Why Nordic']}
						aria-expanded={currentSubNavOpened === 'Why Nordic'}
						aria-haspopup="menu"
						className={`${ ns }__parent-menu-item`}
						ref={node => {
							parentMenuButtonRefs.current['Why Nordic'] = node;
						}}
						onClick={e => { return openSubNav(e, 'Why Nordic'); }}
						tabIndex={isParentMenuInteractive ? 0 : -1}
						type="button"
					>
						<p>Why Nordic</p>
						{renderIcon('carrot-right')}
					</button>

					{/*
					<div className={`${ ns }__menu-item`}>
						<Link
							to={`/`}
							variant={'white'}
							onClick={() => {
								setMobileNavOpened(false);
								onFlyoutCartOpen();
							}}
						>
							{renderIcon('cart')}
							<p>Bag</p>
						</Link>
					</div>
					*/}

					<div className={`${ ns }__menu-item`}>
						<Link
							to={auth.isAuthenticated ? `/account/my-account` : `/account/login`}
							variant="white"
							ref={accountLinkRef}
							onClick={() => { closeAllNavs(); }}
							tabIndex={isParentMenuInteractive ? 0 : -1}
						>
							{renderIcon('account')}
							<p>{auth.isAuthenticated ? 'Account' : 'Sign in'}</p>
						</Link>
					</div>
					{!auth.isAuthenticated && (
						<div className={`${ ns }__menu-item ${ ns }__menu-item--create-account`}>
							<Link
								to="/account/register"
								variant="white"
								ref={createAccountLinkRef}
								onClick={() => { closeAllNavs(); }}
								tabIndex={isParentMenuInteractive ? 0 : -1}
							>
								<p>Create Account</p>
							</Link>
						</div>
					)}
				</div>
				<div
					aria-hidden={!isShopSubmenuInteractive}
					className={`${ ns }__submenu ${ currentSubNavOpened === 'Shop' ? 'opened' : '' }`}
					id={submenuControlIds.Shop}
					aria-label="Shop submenu"
					tabIndex={-1}
				>
					<button
						ref={node => {
							submenuBackButtonRefs.current.Shop = node;
						}}
						onClick={() => { return closeSubNav(); }}
						tabIndex={isShopSubmenuInteractive ? 0 : -1}
						type="button"
					>
						{renderIcon('carrot-left')}
						ALL
					</button>

					<div className={`${ ns }__submenu-section ${ menuItems.shop[0].highlight ? 'highlight-first' : '' }`}>
						<p className="title">Shop</p>
						{menuItems.shop[0] && menuItems.shop[0].links.map((value, index) => {
							return (
								<Link
									key={value.label}
									className="nav-link"
									to={value.url}
									variant="white"
									ref={node => {
										setSubmenuLinkRef('Shop', index, node);
									}}
									onClick={() => { return (closeAllNavs()); }}
									tabIndex={isShopSubmenuInteractive ? 0 : -1}
								>
									{value.label}
								</Link>
							);
						})}
					</div>
					{menuItems.shop.map((value, index) => {
						if (index > 0) {
							return (
								<div className={`${ ns }__submenu-section`} key={value.title}>
									<p className="title">{value.title}</p>
									{value.links.map((link, linkIndex) => {
										const submenuLinkIndex = getShopSectionLinkRefIndex(
											index,
											linkIndex
										);

										return (
											<Link
												className="nav-link"
												to={link.url}
												key={link.label}
												variant="white"
												ref={node => {
													setSubmenuLinkRef('Shop', submenuLinkIndex, node);
												}}
												onClick={() => { return closeAllNavs(); }}
												tabIndex={isShopSubmenuInteractive ? 0 : -1}
											>
												{link.label}
											</Link>
										);
									})}
								</div>
							);
						}
						return true;
					})}

				</div>
				<div
					aria-hidden={!isWhyNordicSubmenuInteractive}
					className={`${ ns }__submenu ${ currentSubNavOpened === 'Why Nordic' ? 'opened' : '' }`}
					id={submenuControlIds['Why Nordic']}
					aria-label="Why Nordic submenu"
					tabIndex={-1}
				>
					<button
						ref={node => {
							submenuBackButtonRefs.current['Why Nordic'] = node;
						}}
						onClick={() => { return closeSubNav(); }}
						tabIndex={isWhyNordicSubmenuInteractive ? 0 : -1}
						type="button"
					>
						{renderIcon('carrot-left')}
						ALL
					</button>
					<div className={`${ ns }__submenu-section`}>
						<p className="title">Why Nordic</p>
						{menuItems.whyNordic[0].links.map((link, index) => {
							const clickHandler = () => {
								closeAllNavs();
							};

							return (
								<Link
									key={link.label}
									className="nav-link"
									to={link.url}
									variant="white"
									ref={node => {
										setSubmenuLinkRef('Why Nordic', index, node);
									}}
									onClick={clickHandler}
									tabIndex={isWhyNordicSubmenuInteractive ? 0 : -1}
								>
									{link.label}
								</Link>
							);
						})}

					</div>
				</div>
				<div
					aria-hidden={!isLearnSubmenuInteractive}
					className={`${ ns }__submenu ${ currentSubNavOpened === 'Learn' ? 'opened' : '' }`}
					id={submenuControlIds.Learn}
					aria-label="Learn submenu"
					tabIndex={-1}
				>
					<button
						ref={node => {
							submenuBackButtonRefs.current.Learn = node;
						}}
						onClick={() => { return closeSubNav(); }}
						tabIndex={isLearnSubmenuInteractive ? 0 : -1}
						type="button"
					>
						{renderIcon('carrot-left')}
						ALL
					</button>
					<div className={`${ ns }__submenu-section`}>
						<p className="title">Learn</p>
						{menuItems.learn[0] && menuItems.learn[0].links.map((link, index) => {
							let linkUrl = link.url;
							let clickHandler = () => {
								closeAllNavs();
							};

							//	--------------------------
							//	Protected Content Handling
							//	--------------------------
							const isProtectedContent = /gateway\.on24\.com/.test(linkUrl);

							if (isProtectedContent && !isUserStatusActive) {
								const message = getMessagingForProtectedContent(false, true);
								linkUrl = '#';
								clickHandler = () => {
									// eslint-disable-next-line no-alert
									window.alert(message);
									closeAllNavs();
								};
							}

							return (
								<Link
									key={link.label}
									className="nav-link"
									to={linkUrl}
									variant="white"
									ref={node => {
										setSubmenuLinkRef('Learn', index, node);
									}}
									onClick={clickHandler}
									tabIndex={isLearnSubmenuInteractive ? 0 : -1}
								>
									{link.label}
								</Link>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
};

export default NavMobileMenu;
